import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import ReactCrop, { Crop, PixelCrop, PercentCrop, makeAspectCrop, centerCrop, convertToPixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import './ImageModal.css';

interface ImageModalProps {
  isOpen: boolean;
  imageUrl: string;
  alt: string;
  onClose: () => void;
  onSave?: (editedImageUrl: string) => void; // Callback opcional para guardar la imagen editada
  allowEdit?: boolean; // Permitir edición (por defecto true)
  isLogo?: boolean; // Si es true, muestra botones adicionales para cambiar/eliminar logo
  onLogoChange?: (file: File) => void; // Callback para cambiar logo (recibe el archivo seleccionado)
  onLogoDelete?: () => void; // Callback para eliminar logo
  isPhoto?: boolean; // Si es true, muestra botones adicionales para cambiar/eliminar foto
  onPhotoChange?: () => void; // Callback para cambiar foto (abrir input file)
  onPhotoDelete?: () => void; // Callback para eliminar foto
}

const ImageModal: React.FC<ImageModalProps> = ({ 
  isOpen, 
  imageUrl, 
  alt, 
  onClose, 
  onSave,
  allowEdit = true,
  isLogo = false,
  onLogoChange,
  onLogoDelete,
  isPhoto = false,
  onPhotoChange,
  onPhotoDelete
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isCropMode, setIsCropMode] = useState(false);
  const [isScaleMode, setIsScaleMode] = useState(false);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [scale, setScale] = useState(100); // Escala en porcentaje (100% = tamaño original)
  const [imageLoaded, setImageLoaded] = useState(false);
  const [originalImageSize, setOriginalImageSize] = useState<{ width: number; height: number } | null>(null);
  const [newWidth, setNewWidth] = useState<number | ''>('');
  const [newHeight, setNewHeight] = useState<number | ''>('');
  const [currentImageUrl, setCurrentImageUrl] = useState<string>(imageUrl); // URL actual de la imagen (puede estar recortada)
  const [originalImageUrlOnEdit, setOriginalImageUrlOnEdit] = useState<string | null>(null); // URL original cuando se entra en modo edición
  const imgRef = useRef<HTMLImageElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isDraggingRef = useRef(false);
  const cropDuringDragRef = useRef<Crop | undefined>(undefined);
  const lastCropUpdateRef = useRef<number>(0);
  const imageLoadCountRef = useRef<number>(0);
  const savedImageDisplaySizeRef = useRef<{ width: number; height: number } | null>(null);

  const aspect = undefined; // Sin restricción de aspecto

  // Función para crear un crop inicial que cubra toda la imagen (100%)
  const createInitialCrop = useCallback(
    (mediaWidth: number, mediaHeight: number) => {
      // Crear un crop inicial del 100% - cubre toda la imagen desde los bordes exactos
      return {
        unit: '%' as const,
        x: 0,
        y: 0,
        width: 100,
        height: 100,
      };
    },
    []
  );

  const onImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const img = e.currentTarget;
      imageLoadCountRef.current += 1;
      
      // Solo procesar la primera carga o si realmente cambió el tamaño
      if (imageLoadCountRef.current === 1 || !imageLoaded) {
        setImageLoaded(true);
        setOriginalImageSize({ width: img.naturalWidth, height: img.naturalHeight });
      }
      
      // Si estamos en modo crop y tenemos un tamaño guardado, aplicarlo
      if (isCropMode && savedImageDisplaySizeRef.current) {
        // El tamaño ya se aplica en el style inline, pero asegurémonos de que se mantenga

      }
      
      // Crear crop inicial del 100% (toda la imagen) solo si estamos en modo recorte, no hay crop existente, y la imagen tiene dimensiones válidas
      if (isCropMode && !crop && img.width > 0 && img.height > 0 && imageLoadCountRef.current <= 2) {
        const initialCrop = createInitialCrop(img.width, img.height);

        setCrop(initialCrop);
      }
    },
    [isCropMode, createInitialCrop, imageLoaded, crop]
  );

  // Callback para onChange del crop - usar requestAnimationFrame para suavizar
  const handleCropChange = useCallback((c: Crop) => {
    if (c) {
      
      // Si no estamos arrastrando pero el crop cambió significativamente, probablemente empezó un arrastre
      if (!isDraggingRef.current && crop) {
        const cropChanged = 
          Math.abs((c.x || 0) - (crop.x || 0)) > 0.1 ||
          Math.abs((c.y || 0) - (crop.y || 0)) > 0.1 ||
          Math.abs((c.width || 0) - (crop.width || 0)) > 0.1 ||
          Math.abs((c.height || 0) - (crop.height || 0)) > 0.1;
        
        if (cropChanged) {

          isDraggingRef.current = true;
        }
      }
      
      // Siempre actualizar la referencia
      cropDuringDragRef.current = c;
      
      // Usar requestAnimationFrame para actualizar el estado de manera suave
      requestAnimationFrame(() => {
        setCrop(c);
      });
    }
  }, [crop]);

  // Callback para onComplete del crop - se ejecuta cuando el usuario termina de ajustar el crop
  const handleCropComplete = useCallback((pixelCrop: PixelCrop, percentCrop: PercentCrop) => {
    if (pixelCrop && imgRef.current) {
      // Asegurarse de que tenemos las dimensiones naturales de la imagen
      const naturalWidth = imgRef.current.naturalWidth;
      const naturalHeight = imgRef.current.naturalHeight;
      
      // Obtener las dimensiones visuales de la imagen
      const displayWidth = imgRef.current.width;
      const displayHeight = imgRef.current.height;

      if (!naturalWidth || !naturalHeight || naturalWidth <= 0 || naturalHeight <= 0) {

        return;
      }

      if (!displayWidth || !displayHeight || displayWidth <= 0 || displayHeight <= 0) {

        return;
      }

      // Validar que el pixelCrop tenga valores válidos
      if (typeof pixelCrop.x !== 'number' || isNaN(pixelCrop.x) ||
          typeof pixelCrop.y !== 'number' || isNaN(pixelCrop.y) ||
          typeof pixelCrop.width !== 'number' || isNaN(pixelCrop.width) ||
          typeof pixelCrop.height !== 'number' || isNaN(pixelCrop.height)) {

        return;
      }

      // ReactCrop proporciona pixelCrop basado en las dimensiones visuales de la imagen
      // Necesitamos convertir estas coordenadas visuales a coordenadas naturales
      const scaleX = naturalWidth / displayWidth;
      const scaleY = naturalHeight / displayHeight;

      // Convertir el crop visual a crop natural
      const naturalCrop: PixelCrop = {
        x: Math.round(pixelCrop.x * scaleX),
        y: Math.round(pixelCrop.y * scaleY),
        width: Math.round(pixelCrop.width * scaleX),
        height: Math.round(pixelCrop.height * scaleY),
        unit: 'px' as const,
      };

      // Asegurarse de que el crop esté dentro de los límites naturales
      const adjustedCrop: PixelCrop = {
        x: Math.max(0, Math.min(naturalCrop.x, naturalWidth - 1)),
        y: Math.max(0, Math.min(naturalCrop.y, naturalHeight - 1)),
        width: Math.min(naturalCrop.width, naturalWidth - Math.max(0, Math.min(naturalCrop.x, naturalWidth - 1))),
        height: Math.min(naturalCrop.height, naturalHeight - Math.max(0, Math.min(naturalCrop.y, naturalHeight - 1))),
        unit: 'px' as const,
      };

      // Actualizar completedCrop con el crop ajustado
      setCompletedCrop(adjustedCrop);
    }
  }, []);

  // Manejar eventos de mouse para detectar cuando realmente termina el arrastre
  useEffect(() => {
    let dragTimeout: NodeJS.Timeout | null = null;

    const handleMouseDown = (e: MouseEvent | TouchEvent) => {
      // Solo activar si el evento es en un handle o en el área de crop
      const target = e.target as HTMLElement;
      const isHandle = target.closest('.ReactCrop__drag-handle');
      const isCropArea = target.closest('.ReactCrop__crop-selection');

      if (isHandle || isCropArea) {
        isDraggingRef.current = true;
        cropDuringDragRef.current = crop;

      }
    };

    const handleMouseUp = () => {
      
      // Usar un pequeño delay para asegurar que el último onChange se haya procesado
      if (dragTimeout) {
        clearTimeout(dragTimeout);
      }
      
      dragTimeout = setTimeout(() => {
        if (isDraggingRef.current && imgRef.current && cropDuringDragRef.current) {
          // Cuando se suelta el mouse, actualizar el estado con el crop final
          const finalCrop = cropDuringDragRef.current;
          const naturalWidth = imgRef.current.naturalWidth;
          const naturalHeight = imgRef.current.naturalHeight;

          if (!naturalWidth || !naturalHeight || naturalWidth <= 0 || naturalHeight <= 0) {

            isDraggingRef.current = false;
            cropDuringDragRef.current = undefined;
            return;
          }

          isDraggingRef.current = false;
          setCrop(finalCrop);
          
          try {
            const pixelCrop = convertToPixelCrop(finalCrop, naturalWidth, naturalHeight);
            
            // Validar el pixelCrop calculado
            if (pixelCrop && 
                typeof pixelCrop.x === 'number' && !isNaN(pixelCrop.x) &&
                typeof pixelCrop.y === 'number' && !isNaN(pixelCrop.y) &&
                typeof pixelCrop.width === 'number' && !isNaN(pixelCrop.width) && pixelCrop.width > 0 &&
                typeof pixelCrop.height === 'number' && !isNaN(pixelCrop.height) && pixelCrop.height > 0) {

              setCompletedCrop(pixelCrop);
            } else {

            }
          } catch (error) {

          }
          
          cropDuringDragRef.current = undefined;
        }
      }, 50);
    };

    if (isCropMode) {
      document.addEventListener('mousedown', handleMouseDown);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchstart', handleMouseDown);
      document.addEventListener('touchend', handleMouseUp);
    }

    return () => {
      if (dragTimeout) {
        clearTimeout(dragTimeout);
      }
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchstart', handleMouseDown);
      document.removeEventListener('touchend', handleMouseUp);
    };
  }, [isCropMode, crop]);

  // Actualizar currentImageUrl cuando cambia imageUrl
  // Solo actualizar si no estamos en modo edición para no sobrescribir cambios temporales
  useEffect(() => {
    if (!isEditing) {
      setCurrentImageUrl(imageUrl);
      setOriginalImageUrlOnEdit(null);
    }
  }, [imageUrl, isEditing]);

  // Manejar cambio de archivo cuando es logo
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {

    const file = e.target.files?.[0];
    if (file && onLogoChange) {

      // Crear preview inmediato
      const reader = new FileReader();
      reader.onloadend = () => {
        const previewUrl = reader.result as string;

        setCurrentImageUrl(previewUrl);
        // Resetear el estado de edición
        setIsEditing(false);
        setCrop(undefined);
        setCompletedCrop(undefined);
        setZoom(1);
        setRotation(0);
        setScale(100);
      };
      reader.readAsDataURL(file);
      
      // Llamar al callback para subir el archivo

      onLogoChange(file);
      
      // Resetear el input
      if (e.target) {
        e.target.value = '';
      }
    } else {

    }
  };

  // Deshabilitar scroll del body cuando el modal está abierto
  // Prevenir scroll del body cuando el modal está abierto y restaurarlo al cerrar
  useEffect(() => {
    if (isOpen) {
      // Guardar el scroll actual del body
      const scrollY = window.scrollY;
      const body = document.body;
      const html = document.documentElement;

      // Guardar todos los estilos originales del body
      const originalOverflow = body.style.overflow || '';
      const originalOverflowY = body.style.overflowY || '';
      const originalOverflowX = body.style.overflowX || '';
      const originalPosition = body.style.position || '';
      const originalTop = body.style.top || '';
      const originalWidth = body.style.width || '';
      const originalHeight = body.style.height || '';
      const originalTouchAction = body.style.touchAction || '';
      
      // Prevenir scroll del body
      body.style.overflow = 'hidden';
      body.style.overflowY = 'hidden';
      body.style.overflowX = 'hidden';
      body.style.position = 'fixed';
      body.style.top = `-${scrollY}px`;
      body.style.width = '100%';
      body.style.height = '100%';
      
      // Cleanup: restaurar el scroll cuando se cierra el modal
      return () => {
        // Restaurar todos los estilos
        if (originalOverflow === '') {
          body.style.overflow = '';
        } else {
          body.style.overflow = originalOverflow;
        }
        if (originalOverflowY === '') {
          body.style.overflowY = '';
        } else {
          body.style.overflowY = originalOverflowY;
        }
        if (originalOverflowX === '') {
          body.style.overflowX = '';
        } else {
          body.style.overflowX = originalOverflowX;
        }
        if (originalPosition === '') {
          body.style.position = '';
        } else {
          body.style.position = originalPosition;
        }
        if (originalTop === '') {
          body.style.top = '';
        } else {
          body.style.top = originalTop;
        }
        if (originalWidth === '') {
          body.style.width = '';
        } else {
          body.style.width = originalWidth;
        }
        if (originalHeight === '') {
          body.style.height = '';
        } else {
          body.style.height = originalHeight;
        }
        if (originalTouchAction === '') {
          body.style.touchAction = '';
        } else {
          body.style.touchAction = originalTouchAction;
        }

        // Restaurar la posición del scroll
        window.scrollTo(0, scrollY);

        // Forzar un reflow para asegurar que los cambios se apliquen
        void body.offsetHeight;
      };
    }
  }, [isOpen]);

  // Manejar cambio de escala (tamaño de salida)
  const handleScaleChange = (newScale: number) => {
    setScale(newScale);
  };

  // Manejar toggle del modo escala
  const handleScaleModeToggle = () => {
    const newScaleMode = !isScaleMode;
    setIsScaleMode(newScaleMode);
    if (newScaleMode && originalImageSize) {
      // Inicializar con las dimensiones originales
      setNewWidth(originalImageSize.width);
      setNewHeight(originalImageSize.height);
    } else {
      setNewWidth('');
      setNewHeight('');
    }
    // Si se desactiva el modo escala, restaurar a 100%
    if (!newScaleMode) {
      setScale(100);
    }
  };

  // Manejar cambio de ancho manteniendo el aspecto
  const handleWidthChange = (value: number) => {
    if (originalImageSize && value > 0 && value <= originalImageSize.width) {
      setNewWidth(value);
      // Calcular altura manteniendo el aspecto
      const aspectRatio = originalImageSize.height / originalImageSize.width;
      const calculatedHeight = Math.round(value * aspectRatio);
      setNewHeight(calculatedHeight);
      // Calcular el porcentaje de escala
      const scalePercent = Math.round((value / originalImageSize.width) * 100);
      setScale(scalePercent);
    }
  };

  // Manejar cambio de altura manteniendo el aspecto
  const handleHeightChange = (value: number) => {
    if (originalImageSize && value > 0 && value <= originalImageSize.height) {
      setNewHeight(value);
      // Calcular ancho manteniendo el aspecto
      const aspectRatio = originalImageSize.width / originalImageSize.height;
      const calculatedWidth = Math.round(value * aspectRatio);
      setNewWidth(calculatedWidth);
      // Calcular el porcentaje de escala
      const scalePercent = Math.round((value / originalImageSize.height) * 100);
      setScale(scalePercent);
    }
  };

  // Aplicar escala
  const handleApplyScale = async () => {
    if (!newWidth || !newHeight || !originalImageSize || !imgRef.current) {

      return;
    }

    try {
      // Crear un crop del 100% de la imagen original con la nueva escala
      const fullCrop: PixelCrop = {
        x: 0,
        y: 0,
        width: originalImageSize.width,
        height: originalImageSize.height,
        unit: 'px' as const,
      };

      // Calcular el porcentaje de escala basado en las nuevas dimensiones
      const scalePercent = Math.round((newWidth / originalImageSize.width) * 100);
      
      const scaledImageUrl = await getCroppedImg(
        currentImageUrl,
        fullCrop,
        0, // sin rotación
        scalePercent
      );

      // Actualizar la URL de la imagen con la versión escalada
      setCurrentImageUrl(scaledImageUrl);
      // Actualizar el tamaño original con las nuevas dimensiones
      setOriginalImageSize({ width: newWidth, height: newHeight });
      // Salir del modo escala
      setIsScaleMode(false);
      // Resetear los campos de entrada
      setNewWidth('');
      setNewHeight('');
      setScale(100);
    } catch (error) {

      const errorMessage = error instanceof Error ? error.message : 'Error desconocido';

      // Error silencioso - no mostrar diálogo al usuario
    }
  };

  // Función para obtener la URL del proxy si es necesario
  const getProxyUrlIfNeeded = (url: string): string => {
    // No procesar URLs blob (son locales y no necesitan proxy)
    if (!url || url.startsWith('blob:') || url.startsWith('data:')) {
      return url;
    }
    const wordpressDomain = 'smartcityexposantiagodelestero.com';
    if (!url.includes(wordpressDomain) || url.includes('/api/proxy-image')) {
      return url;
    }
    let proxyBaseUrl = process.env.REACT_APP_API_URL || window.location.origin;
    // Normalizar: eliminar /api al final si existe para evitar duplicación
    proxyBaseUrl = proxyBaseUrl.replace(/\/api\/?$/, '');
    return `${proxyBaseUrl}/api/proxy-image?url=${encodeURIComponent(url)}`;
  };

  const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', (error) => reject(error));
      image.setAttribute('crossOrigin', 'anonymous');
      // Usar la URL del proxy si es necesario para evitar CORS
      const proxyUrl = getProxyUrlIfNeeded(url);
      image.src = proxyUrl;
    });

  const getCroppedImg = async (
    imageSrc: string,
    pixelCrop: PixelCrop,
    rotation: number = 0,
    outputScale: number = 100
  ): Promise<string> => {
    // Validar que el crop tenga valores válidos
    if (!pixelCrop) {
      throw new Error('Crop no definido');
    }

    // Validar que todos los valores sean números válidos
    if (typeof pixelCrop.x !== 'number' || isNaN(pixelCrop.x) ||
        typeof pixelCrop.y !== 'number' || isNaN(pixelCrop.y) ||
        typeof pixelCrop.width !== 'number' || isNaN(pixelCrop.width) ||
        typeof pixelCrop.height !== 'number' || isNaN(pixelCrop.height)) {
      throw new Error(`Crop con valores inválidos: x=${pixelCrop.x}, y=${pixelCrop.y}, w=${pixelCrop.width}, h=${pixelCrop.height}`);
    }

    if (pixelCrop.width <= 0 || pixelCrop.height <= 0) {
      throw new Error(`Crop inválido: dimensiones deben ser mayores que 0 (w=${pixelCrop.width}, h=${pixelCrop.height})`);
    }

    let image: HTMLImageElement;
    try {
      image = await createImage(imageSrc);
    } catch (error) {
      throw new Error(`Error al cargar la imagen: ${error instanceof Error ? error.message : 'Error desconocido'}`);
    }

    // Validar que la imagen se haya cargado correctamente
    if (!image.width || !image.height || image.width <= 0 || image.height <= 0) {
      throw new Error(`Imagen con dimensiones inválidas: ${image.width}x${image.height}`);
    }
    
    // Ajustar el crop para que esté dentro de los límites de la imagen
    const cropX = Math.max(0, Math.min(pixelCrop.x, image.width - 1));
    const cropY = Math.max(0, Math.min(pixelCrop.y, image.height - 1));
    const cropWidth = Math.min(pixelCrop.width, image.width - cropX);
    const cropHeight = Math.min(pixelCrop.height, image.height - cropY);

    if (cropWidth <= 0 || cropHeight <= 0) {
      throw new Error(`Crop ajustado inválido: imagen ${image.width}x${image.height}, crop ajustado x:${cropX} y:${cropY} w:${cropWidth} h:${cropHeight}`);
    }

    // Usar el crop ajustado
    const adjustedCrop: PixelCrop = {
      x: cropX,
      y: cropY,
      width: cropWidth,
      height: cropHeight,
      unit: 'px' as const,
    };
    
    // Aplicar escala al tamaño de salida usando el crop ajustado
    const outputWidth = Math.round((adjustedCrop.width * outputScale) / 100);
    const outputHeight = Math.round((adjustedCrop.height * outputScale) / 100);

    // Validar que las dimensiones de salida sean válidas
    if (outputWidth <= 0 || outputHeight <= 0) {
      throw new Error(`Dimensiones de salida inválidas: ${outputWidth}x${outputHeight}`);
    }

    // Crear canvas final con el tamaño de salida escalado
    const canvas = document.createElement('canvas');
    canvas.width = outputWidth;
    canvas.height = outputHeight;
    const ctx = canvas.getContext('2d', { 
      alpha: true,  // Preservar transparencia
      willReadFrequently: false,
      desynchronized: false
    });

    if (!ctx) {
      throw new Error('No 2d context');
    }

    // Limpiar el canvas para asegurar transparencia (no dibujar fondo)
    // Usar clearRect para eliminar cualquier contenido previo
    ctx.clearRect(0, 0, outputWidth, outputHeight);
    
    // Asegurar que no se dibuje ningún fondo
    ctx.globalCompositeOperation = 'source-over';

    // Si hay rotación, aplicar primero la rotación
    if (rotation !== 0) {
      const maxSize = Math.max(image.width, image.height);
      const safeArea = 2 * ((maxSize / 2) * Math.sqrt(2));

      // Canvas temporal para rotación
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = safeArea;
      tempCanvas.height = safeArea;
      const tempCtx = tempCanvas.getContext('2d', { 
        alpha: true,  // Preservar transparencia
        willReadFrequently: false,
        desynchronized: false
      });

      if (!tempCtx) {
        throw new Error('No 2d context');
      }

      // Limpiar el canvas temporal para asegurar transparencia
      tempCtx.clearRect(0, 0, safeArea, safeArea);
      
      // Asegurar que no se dibuje ningún fondo
      tempCtx.globalCompositeOperation = 'source-over';

      // Rotar la imagen en el canvas temporal
      tempCtx.translate(safeArea / 2, safeArea / 2);
      tempCtx.rotate((rotation * Math.PI) / 180);
      tempCtx.translate(-safeArea / 2, -safeArea / 2);

      tempCtx.drawImage(
        image,
        safeArea / 2 - image.width * 0.5,
        safeArea / 2 - image.height * 0.5
      );

      // Dibujar el área recortada escalada desde el canvas rotado
      // Asegurar que se preserve la transparencia al dibujar
      ctx.save();
      ctx.globalCompositeOperation = 'source-over';
      ctx.drawImage(
        tempCanvas,
        adjustedCrop.x,
        adjustedCrop.y,
        adjustedCrop.width,
        adjustedCrop.height,
        0,
        0,
        outputWidth,
        outputHeight
      );
      ctx.restore();
    } else {
      // Sin rotación, dibujar directamente
      // Asegurar que se preserve la transparencia al dibujar
      ctx.save();
      ctx.globalCompositeOperation = 'source-over';
      ctx.drawImage(
        image,
        adjustedCrop.x,
        adjustedCrop.y,
        adjustedCrop.width,
        adjustedCrop.height,
        0,
        0,
        outputWidth,
        outputHeight
      );
      ctx.restore();
    }

    return new Promise((resolve) => {
      // Detectar si la imagen original tiene transparencia
      // Verificar la URL de origen y el tipo de la imagen cargada
      const imageSrcLower = imageSrc.toLowerCase();
      const isPngSource = imageSrcLower.includes('.png') || 
                          imageSrcLower.includes('image/png') ||
                          imageSrcLower.startsWith('data:image/png') ||
                          imageSrcLower.includes('blob:') || // Los blobs pueden ser PNG
                          imageSrcLower.includes('proxy-image'); // Las imágenes del proxy pueden ser PNG
      
      // Verificar también la URL de la imagen cargada (puede ser del proxy)
      const loadedImageSrcLower = image.src.toLowerCase();
      const isPngLoaded = loadedImageSrcLower.includes('.png') || 
                          loadedImageSrcLower.includes('image/png') ||
                          loadedImageSrcLower.startsWith('data:image/png');
      
      // Si la imagen cargada tiene dimensiones y es PNG, o si la URL indica PNG
      // Por defecto, si no podemos determinar con certeza, usar PNG para preservar transparencia
      // Esto es más seguro para evitar perder transparencia
      const hasTransparency = isPngSource || isPngLoaded || 
                              (image.complete && (image.src.toLowerCase().includes('.png') || 
                                                  image.src.toLowerCase().includes('image/png')));
      
      // Si no podemos determinar con certeza, usar PNG por defecto para preservar transparencia
      // Es mejor preservar la transparencia que perderla
      if (hasTransparency || imageSrcLower.includes('blob:') || imageSrcLower.includes('data:')) {
        // Usar PNG para preservar transparencia
        canvas.toBlob((blob) => {
          if (blob) {
            const url = URL.createObjectURL(blob);
            resolve(url);
          } else {
            // Fallback a JPEG si falla (no debería pasar)

            canvas.toBlob((jpegBlob) => {
              if (jpegBlob) {
                const url = URL.createObjectURL(jpegBlob);
                resolve(url);
              }
            }, 'image/jpeg', 0.95);
          }
        }, 'image/png', 1.0);
      } else {
        // Solo usar JPEG si estamos 100% seguros de que no hay transparencia
        // Para imágenes JPG explícitas
        if (imageSrcLower.includes('.jpg') || imageSrcLower.includes('.jpeg') || 
            imageSrcLower.includes('image/jpeg')) {
          canvas.toBlob((blob) => {
            if (blob) {
              const url = URL.createObjectURL(blob);
              resolve(url);
            }
          }, 'image/jpeg', 0.95);
        } else {
          // Por defecto, usar PNG para preservar cualquier transparencia
          canvas.toBlob((blob) => {
            if (blob) {
              const url = URL.createObjectURL(blob);
              resolve(url);
            }
          }, 'image/png', 1.0);
        }
      }
    });
  };

  const handleSave = async () => {
    if (!imgRef.current) {
      return;
    }

    try {
      let finalImageUrl: string;

      // Si hay crop, aplicar recorte
      if (completedCrop) {
        finalImageUrl = await getCroppedImg(
          currentImageUrl,
          completedCrop,
          rotation,
          scale
        );
      } else if (scale < 100 || rotation !== 0) {
        // Si no hay crop pero hay escala o rotación, crear un crop del 100% y aplicar transformaciones
        if (originalImageSize && imgRef.current) {
          const fullCrop: PixelCrop = {
            x: 0,
            y: 0,
            width: originalImageSize.width,
            height: originalImageSize.height,
            unit: 'px' as const,
          };
          finalImageUrl = await getCroppedImg(
            currentImageUrl,
            fullCrop,
            rotation,
            scale
          );
        } else {
          // Si no hay cambios, usar la URL actual
          finalImageUrl = currentImageUrl;
        }
      } else {
        // Si no hay cambios aplicados, generar un blob de la imagen actual para asegurar que sea un blob
        // Esto es necesario porque la URL original puede ser de WordPress y no un blob
        if (originalImageSize && imgRef.current) {
          const fullCrop: PixelCrop = {
            x: 0,
            y: 0,
            width: originalImageSize.width,
            height: originalImageSize.height,
            unit: 'px' as const,
          };
          finalImageUrl = await getCroppedImg(
            currentImageUrl,
            fullCrop,
            0, // sin rotación
            100 // sin escala
          );
        } else {
          // Fallback: usar la URL actual
          finalImageUrl = currentImageUrl;
        }
      }

      if (onSave) {
        onSave(finalImageUrl);
      }

      // Limpiar la URL original guardada ya que los cambios se aplicaron
      setOriginalImageUrlOnEdit(null);
      setIsEditing(false);
      setIsCropMode(false);
      setIsScaleMode(false);
      setCrop(undefined);
      setCompletedCrop(undefined);
      setZoom(1);
      setRotation(0);
      setScale(100);
      setNewWidth('');
      setNewHeight('');
    } catch (error) {

      // Error silencioso - no mostrar diálogo al usuario
    }
  };

  const handleCancel = () => {
    // Restaurar la imagen original si se había guardado
    if (originalImageUrlOnEdit) {
      setCurrentImageUrl(originalImageUrlOnEdit);
      setOriginalImageUrlOnEdit(null);
    }
    setIsEditing(false);
    setIsCropMode(false);
    setIsScaleMode(false);
    setCrop(undefined);
    setCompletedCrop(undefined);
    setZoom(1);
    setRotation(0);
    setScale(100);
    setNewWidth('');
    setNewHeight('');
  };

  const handleCropModeToggle = () => {
    const newCropMode = !isCropMode;
    
    // Guardar el tamaño visual de la imagen ANTES de cambiar el modo
    // Necesitamos calcular el tamaño BASE (sin zoom) para que ReactCrop lo use correctamente
    if (newCropMode && !isCropMode && imgRef.current) {
      const rect = imgRef.current.getBoundingClientRect();
      // El tamaño visual incluye el zoom, así que calculamos el tamaño base
      // dividiendo por el zoom actual (si es mayor que 1)
      const baseWidth = zoom > 1 ? rect.width / zoom : rect.width;
      const baseHeight = zoom > 1 ? rect.height / zoom : rect.height;
      savedImageDisplaySizeRef.current = {
        width: baseWidth,
        height: baseHeight
      };

    }
    
    setIsCropMode(newCropMode);
    
    // Resetear zoom y rotación cuando se activa el modo crop
    if (newCropMode) {
      setZoom(1);
      setRotation(0);
    }
    
    if (newCropMode && imgRef.current && imgRef.current.complete) {
      // Al activar el modo recorte, crear un crop inicial del 100% (toda la imagen) solo si la imagen ya está cargada
      const { width, height } = imgRef.current;
      if (width > 0 && height > 0) {
        const initialCrop = createInitialCrop(width, height);

        setCrop(initialCrop);
      }
    } else if (!newCropMode) {
      // Al desactivar, limpiar el crop

      setCrop(undefined);
      setCompletedCrop(undefined);
      savedImageDisplaySizeRef.current = null;
    }
  };

  const handleApplyCrop = async () => {
    // Aplicar el recorte generando la imagen recortada
    if (!completedCrop || !imgRef.current) {

      setIsCropMode(false);
      return;
    }

    // Validar que el crop tenga dimensiones válidas
    if (completedCrop.width <= 0 || completedCrop.height <= 0) {

      // Error silencioso - no mostrar diálogo al usuario
      return;
    }

    try {
      const croppedImageUrl = await getCroppedImg(
        currentImageUrl,
        completedCrop,
        rotation,
        scale
      );
      // Actualizar la URL de la imagen con la versión recortada
      setCurrentImageUrl(croppedImageUrl);
      // Limpiar el crop ya que ya está aplicado
      setCrop(undefined);
      setCompletedCrop(undefined);
      // Salir del modo crop
      setIsCropMode(false);
      // Resetear rotación y escala ya que se aplicaron
      setRotation(0);
      setScale(100);
    } catch (error) {

      const errorMessage = error instanceof Error ? error.message : 'Error desconocido';

      // Error silencioso - no mostrar diálogo al usuario
    }
  };

  const handleRemoveCrop = () => {
    setCrop(undefined);
    setCompletedCrop(undefined);
    setIsCropMode(false);
  };

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    setImageLoaded(true);
    const img = e.currentTarget;
    setOriginalImageSize({ width: img.naturalWidth, height: img.naturalHeight });
    onImageLoad(e);
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="image-modal-overlay" onClick={!isEditing ? onClose : undefined}>
      <div 
        className={`image-modal-content ${isEditing ? 'editing-mode' : ''}`} 
        onClick={(e) => e.stopPropagation()}
      >
        <button className="image-modal-close" onClick={isEditing ? handleCancel : onClose} aria-label="Cerrar">
          ×
        </button>

        {!isEditing ? (
          <div className="image-modal-preview-container">
            {originalImageSize && (
              <div className="image-size-info">
                {originalImageSize.width} × {originalImageSize.height} px
              </div>
            )}
            <img 
              src={currentImageUrl} 
              alt={alt} 
              className="image-modal-image"
              onLoad={handleImageLoad}
            />
            <div className="image-modal-footer">
              <div className="image-modal-actions">
                {allowEdit && imageLoaded && (
                  <button 
                    className="image-modal-edit-btn" 
                    onClick={() => {
                      // Guardar la URL original antes de entrar en modo edición
                      setOriginalImageUrlOnEdit(currentImageUrl);
                      setIsEditing(true);
                    }}
                    aria-label="Editar imagen"
                  >
                    Editar
                  </button>
                )}
                {isLogo && !isEditing && allowEdit && (
                  <>
                    {onLogoChange && (
                      <>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/png,image/svg+xml,image/svg,.svg,.ai,.eps,application/postscript,application/illustrator"
                          onChange={handleFileChange}
                          style={{ display: 'none' }}
                        />
                        <button 
                          className="image-modal-logo-btn image-modal-logo-change-btn" 
                          onClick={() => {
                            fileInputRef.current?.click();
                          }}
                          aria-label="Cambiar logo"
                        >
                          Cambiar Logo
                        </button>
                      </>
                    )}
                    {onLogoDelete && (
                      <button 
                        className="image-modal-logo-btn image-modal-logo-delete-btn" 
                        onClick={() => {
                          onLogoDelete();
                          onClose();
                        }}
                        aria-label="Eliminar logo"
                      >
                        Eliminar
                      </button>
                    )}
                  </>
                )}
                {isPhoto && !isEditing && allowEdit && (
                  <>
                    {onPhotoChange && (
                      <button 
                        className="image-modal-logo-btn image-modal-logo-change-btn" 
                        onClick={() => {
                          onPhotoChange();
                        }}
                        aria-label="Cambiar foto"
                      >
                        Cambiar Foto
                      </button>
                    )}
                    {onPhotoDelete && (
                      <button 
                        className="image-modal-logo-btn image-modal-logo-delete-btn" 
                        onClick={() => {
                          onPhotoDelete();
                          onClose();
                        }}
                        aria-label="Eliminar foto"
                      >
                        Eliminar
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="image-editor-container">
            <div className="image-editor-toolbar">
              <button
                type="button"
                className={`editor-toolbar-btn ${isCropMode ? 'active' : ''}`}
                onClick={handleCropModeToggle}
              >
                Recortar
              </button>
              <button
                type="button"
                className={`editor-toolbar-btn ${isScaleMode ? 'active' : ''}`}
                onClick={handleScaleModeToggle}
              >
                Escalar
              </button>
            </div>
            
            <div className="image-editor-main-content">
              <div className="image-editor-cropper">
                {isCropMode && crop ? (
                  <div className="crop-container">
                    <ReactCrop
                      crop={crop}
                      onChange={handleCropChange}
                      onComplete={handleCropComplete}
                      aspect={aspect}
                      ruleOfThirds={false}
                      disabled={false}
                    >
                      <img
                        ref={imgRef}
                        alt={alt}
                        src={currentImageUrl}
                        style={{
                          // Sin transformaciones durante el recorte (ReactCrop necesita dimensiones reales)
                          transform: 'none',
                          display: 'block',
                          objectFit: 'contain',
                          // Aplicar el tamaño base guardado para mantener el mismo tamaño visual
                          // pero siempre respetando maxHeight para que la imagen se vea completa
                          ...(savedImageDisplaySizeRef.current ? {
                            maxWidth: '100%',
                            maxHeight: '100%',
                            width: 'auto',
                            height: 'auto'
                          } : {
                            maxWidth: '100%',
                            maxHeight: '100%',
                            width: 'auto',
                            height: 'auto'
                          })
                        }}
                        onLoad={onImageLoad}
                      />
                    </ReactCrop>
                  </div>
                ) : (
                  <div className="image-preview-container">
                    <img 
                      ref={imgRef}
                      src={currentImageUrl} 
                      alt={alt}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain'
                      }}
                      onLoad={onImageLoad}
                    />
                  </div>
                )}
              </div>

              {(isCropMode || isScaleMode) && (
                <div className="controls-panels-container">
                  {isCropMode && (
                    <div className="crop-controls-panel">
                      <div className="crop-controls-header">
                        <h3>RECORTAR IMAGEN</h3>
                      </div>
                      <div className="crop-controls-actions">
                        <button
                          type="button"
                          className="crop-btn crop-btn-apply"
                          onClick={handleApplyCrop}
                          disabled={!completedCrop}
                        >
                          Aplicar recorte
                        </button>
                        <button
                          type="button"
                          className="crop-btn crop-btn-remove"
                          onClick={handleRemoveCrop}
                          disabled={!completedCrop}
                        >
                          Quitar recorte
                        </button>
                      </div>
                    </div>
                  )}

                  {isScaleMode && originalImageSize && (
                    <div className="crop-controls-panel">
                      <div className="crop-controls-header">
                        <h3>ESCALAR IMAGEN</h3>
                      </div>
                      <div className="scale-controls-content">
                        <div className="scale-info">
                          <span>Dimensiones originales {originalImageSize.width} × {originalImageSize.height}</span>
                        </div>
                        <div className="scale-dimensions-input">
                          <label>
                            <span>Nuevas dimensiones:</span>
                            <div className="dimensions-input-group">
                              <input
                                type="number"
                                min={1}
                                max={originalImageSize.width}
                                value={newWidth}
                                onChange={(e) => {
                                  const val = parseInt(e.target.value);
                                  if (!isNaN(val)) {
                                    handleWidthChange(val);
                                  } else {
                                    setNewWidth('');
                                  }
                                }}
                              />
                              <span>×</span>
                              <input
                                type="number"
                                min={1}
                                max={originalImageSize.height}
                                value={newHeight}
                                onChange={(e) => {
                                  const val = parseInt(e.target.value);
                                  if (!isNaN(val)) {
                                    handleHeightChange(val);
                                  } else {
                                    setNewHeight('');
                                  }
                                }}
                              />
                              <button
                                type="button"
                                className="scale-btn"
                                onClick={handleApplyScale}
                                disabled={!newWidth || !newHeight || newWidth === originalImageSize.width && newHeight === originalImageSize.height}
                              >
                                Escalar
                              </button>
                            </div>
                          </label>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="image-editor-actions">
              <button 
                className="editor-btn editor-btn-cancel" 
                onClick={handleCancel}
              >
                Cancelar
              </button>
              <button 
                className="editor-btn editor-btn-save" 
                onClick={handleSave}
              >
                Guardar
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // Renderizar el modal usando un portal directamente en el body
  return createPortal(modalContent, document.body);
};

export default ImageModal;
